import { useCallback, useMemo } from "react";

import { useGenerationSettings } from "~/entities/model";
import { getModelById } from "~/shared/data/chat-models";
import { PROMPT, SPACE } from "~/shared/lib/design-tokens";
import { findPromptNameCollisions } from "~/shared/lib/element-mentions";
import { cn } from "~/shared/lib/utils";
import { useChatSettingsStore } from "~/shared/store/useChatSettingsStore";
import { useChatUIStore } from "~/shared/store/useChatUIStore";

import type { AttachmentCreate } from "~/entities/chat";

import { useChatLayout } from "~/shared/context/ChatLayoutContext";

import { AttachmentPreview } from "./AttachmentPreview";
import { PromptChannelsRow } from "./PromptChannelsRow";
import { PromptComposer } from "./PromptComposer";
import { PromptDialogs } from "./PromptDialogs";
import { PromptDraftNotices, PromptGateNotices } from "./PromptNotices";
import { PromptSettingsSummary } from "./PromptSettingsSummary";
import { useChatAttachments } from "./useChatAttachments";
import { useInputVideoDurationSeconds } from "./useInputVideoDuration";
import { usePromptChannels } from "./usePromptChannels";
import { usePromptDraft } from "./usePromptDraft";
import { usePromptElements } from "./usePromptElements";
import { usePromptQuote } from "./usePromptQuote";
import { usePromptSendGate } from "./usePromptSendGate";
import { useRepeatPrefill } from "./useRepeatPrefill";

interface PromptBoxProps {
    chatId: string | null;
    onSubmit: (prompt: string, attachments: AttachmentCreate[]) => Promise<void> | void;
    disabled?: boolean;
}

export function PromptBox({ chatId, onSubmit, disabled }: PromptBoxProps) {
    const mode = useChatSettingsStore((s) => s.mode);
    const modelId = useChatSettingsStore((s) => s.modelId);
    const audioEnabled = useChatSettingsStore((s) => s.audioEnabled);
    const toggleSettings = useChatUIStore((s) => s.toggleSettingsPanel);

    const { heroMedia } = useChatLayout();
    const settingsQuery = useGenerationSettings();

    const model = getModelById(settingsQuery.data, modelId);
    const currentMode = useMemo(() => model?.modes.find((m) => m.name === mode) ?? null, [model, mode]);

    const attachments = useChatAttachments({ chatId, currentMode });

    // Элементы библиотеки — отдельный канал генерации: показываем только
    // режимам, которые их принимают.
    const elements = usePromptElements({ chatId, maxElements: currentMode?.maxElements ?? null });

    const inputVideoDuration = useInputVideoDurationSeconds(attachments.items);

    // Черновик поднят выше цены: длина промпта — такой же признак исполнимости,
    // как число картинок (у строки каталога свой потолок промпта, и исполнителя
    // отбирают по нему).
    const draft = usePromptDraft({
        chatId,
        items: attachments.items,
        onSubmit,
        disabled,
        hasRequiredFiles: attachments.hasRequiredFiles,
        elements: elements.mentionable,
        // Потолок промпта — свойство режима: длиннее его запрос не исполнит ни один
        // подключённый провайдер, и раньше человек узнавал об этом только по тому,
        // что цена не считается.
        maxPromptLength: currentMode?.maxPromptLength ?? null,
    });

    const quote = usePromptQuote({
        text: draft.text,
        inputImageCount: attachments.kindCounts.image,
        hasLastFrame: attachments.itemByRole.last_frame !== undefined,
        referenceImageCount: attachments.referenceItems.length,
        elementCount: elements.selectedIds.length,
        inputVideoCount: attachments.kindCounts.video,
        inputVideoDurationSeconds: inputVideoDuration.seconds,
    });

    const channels = usePromptChannels({
        currentMode,
        frameItems: attachments.frameItems,
        hasImageRoles: attachments.hasImageRoles,
        imageRoles: attachments.imageRoles,
        itemByRole: attachments.itemByRole,
        referenceImageCount: attachments.referenceItems.length,
        selectedElementCount: elements.selectedIds.length,
        videoAttachmentCount: attachments.kindCounts.video,
        audioEnabled,
    });

    const gate = usePromptSendGate({
        minImages: attachments.minImages,
        uploadedImagesCount: attachments.uploadedImagesCount,
        hasRequiredFiles: attachments.hasRequiredFiles,
        minVisualInputs: attachments.minVisualInputs,
        hasRequiredVisualInputs: attachments.hasRequiredVisualInputs,
        hasElements: elements.selectedIds.length > 0,
        inputsNotExecutable: quote.inputsNotExecutable,
        priceIncomplete: inputVideoDuration.pending,
    });

    // Есть ли что очищать: этим же признаком «Повторить запрос» решает, спрашивать
    // ли подтверждение перед подменой набранного.
    const hasDraft =
        draft.text.trim().length > 0 || attachments.items.length > 0 || elements.selectedIds.length > 0;

    // Имя элемента, совпавшее со словом промпта, Kling связывает неверно. Бэкенд
    // это чинит сам (отправит `СтолA`), поэтому предупреждаем, но не блокируем:
    // человеку показываем, почему стоит переименовать элемент.
    const nameCollisions = useMemo(
        () => findPromptNameCollisions(draft.text, elements.mentionable),
        [draft.text, elements.mentionable],
    );

    // «Повторить запрос»: действие на сообщении оставляет заявку, бар её
    // применяет. Набранное не затираем молча — при непустом черновике сначала
    // спрашиваем.
    const repeatPrefill = useRepeatPrefill({
        chatId,
        hasDraft,
        setText: draft.onTextChange,
        clearAttachments: attachments.clearAttachments,
        library: elements.library,
        libraryLoaded: elements.libraryLoaded,
    });

    // Кнопка «очистить» сбрасывает поле целиком: и текст промпта, и все
    // вложения. Раньше pending-вложения чистились автоматически после отправки,
    // теперь они намеренно остаются, поэтому ручная очистка обязана убирать и
    // их — иначе картинки зависали бы после клика «очистить».
    const handleClearAll = useCallback(() => {
        draft.handleClear();
        attachments.clearAttachments();
        elements.clear();
        repeatPrefill.dismissRepeatNotices();
    }, [draft, attachments, elements, repeatPrefill]);

    // Предупреждение живёт до отправки: человек либо шлёт запрос, зная про
    // потерянный элемент, либо чистит бар. Обе точки снимают его явно.
    const handleSendAll = useCallback(async () => {
        repeatPrefill.dismissRepeatNotices();
        await draft.handleSend();
    }, [draft, repeatPrefill]);

    return (
        <div className="shrink-0 border-t border-white/5 bg-zinc-950">
            <div className={cn(PROMPT.outerY, heroMedia ? SPACE.heroGutter : PROMPT.outerX)}>
                <div className={heroMedia ? undefined : "md:mx-auto md:max-w-4xl"}>
                    <PromptSettingsSummary model={model} onOpenSettings={toggleSettings} />

                    <PromptDraftNotices
                        missingElements={repeatPrefill.missingElements}
                        droppedReferenceImages={repeatPrefill.droppedReferenceImages}
                        nameCollisions={nameCollisions}
                        promptLength={draft.text.length}
                        promptLimit={draft.promptLimit}
                    />

                    <PromptChannelsRow
                        disabled={disabled === true}
                        attachments={attachments}
                        elements={elements}
                        channels={channels}
                        conflictingElementIds={nameCollisions.map((element) => element.id)}
                    />

                    {/* Лента пула — вложения без своей колонки. Референс уже показан
                        в «Референсах», второй раз он был бы не тем же входом, а
                        видимостью двух. В режимах с роль-слотами лента раньше не
                        рисовалась вовсе — и загруженные скрепкой видео с аудио
                        (Seedance omni принимает по три тех и других) не появлялись
                        нигде: файл ушёл в запрос, а человек его не видел. */}
                    <AttachmentPreview
                        items={channels.poolItems}
                        onRemove={attachments.remove}
                        onReorder={attachments.reorder}
                    />

                    <PromptGateNotices
                        requireFilesTooltip={gate.requireFilesTooltip}
                        blockedWithElements={gate.blockedWithElements}
                        remainingImages={gate.remainingImages}
                        missingVisualInput={gate.missingVisualInput}
                        inputsNotExecutable={quote.inputsNotExecutable}
                    />

                    <PromptComposer
                        chatId={chatId}
                        disabled={disabled === true}
                        draft={draft}
                        attachments={attachments}
                        canClear={hasDraft}
                        onClear={handleClearAll}
                        onToggleSettings={toggleSettings}
                        costLabel={quote.costLabel}
                        canSend={draft.canSend && !quote.inputsNotExecutable && !inputVideoDuration.pending}
                        blockedHint={gate.blockedHint}
                        onSend={handleSendAll}
                    />
                </div>
            </div>
            <PromptDialogs
                chatId={chatId}
                attachments={attachments}
                elements={elements}
                repeatPrefill={repeatPrefill}
            />
        </div>
    );
}
